import React, { useEffect, useState } from "react";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import { STAGES, STAGE_KEYS } from "../contract";
import useTags from "../hooks/useTags";
import CrmDialog from "../ui/CrmDialog";
import CrmAlertDialog from "../ui/CrmAlertDialog";
import CrmButton from "../ui/CrmButton";
import CrmSelect from "../ui/CrmSelect";
import CrmInput from "../ui/CrmInput";
import { normaliseTag } from "../ui/TagInput";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The parameter of a bulk action: the tag to add or remove, the stage to set (a manual
 * override, spec "Change stage (manual exception path)"), the sequence to add to.
 * `onConfirm(params)` receives the contract's `params` (`{tag}` / `{stage}` /
 * `{sequence_id}`).
 */
export default function BulkDialog({ action, count, onClose, onConfirm, busy }) {
  const t = useT("crm");
  const { tags } = useTags();
  const [tag, setTag] = useState("");
  const [stage, setStage] = useState("active");
  const [sequences, setSequences] = useState(null);
  const [sequenceId, setSequenceId] = useState("");

  useEffect(() => {
    if (action !== "add_to_sequence") return undefined;
    let alive = true;
    crmApi.listSequences()
      .then((page) => { if (alive) setSequences(Array.isArray(page?.rows) ? page.rows : []); })
      .catch(() => { if (alive) setSequences([]); });
    return () => { alive = false; };
  }, [action]);

  const TITLES = {
    add_tag: t("bulk.addTagTitle", { n: count }),
    remove_tag: t("bulk.removeTagTitle", { n: count }),
    set_stage: t("bulk.stageTitle", { n: count }),
    add_to_sequence: t("bulk.sequenceTitle", { n: count }),
  };
  const params = action === "set_stage" ? { stage }
    : action === "add_to_sequence" ? { sequence_id: sequenceId }
      : { tag: normaliseTag(tag) };
  const ready = action === "set_stage" || (action === "add_to_sequence" ? !!sequenceId : !!normaliseTag(tag));

  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} title={TITLES[action]} size="sm" testId={CRM.clientsBulkDialog}
      footer={(
        <>
          <CrmButton variant="link" onClick={onClose}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={() => onConfirm(params)} disabled={!ready || busy}
            data-testid={action === "add_tag" || action === "remove_tag" ? CRM.clientsBulkTagConfirm : CRM.clientsBulkConfirm}>
            {t("common.apply")}
          </CrmButton>
        </>
      )}>
      {(action === "add_tag" || action === "remove_tag") && (
        <>
          <label htmlFor="crm-bulk-tag" className={LABEL}>{t("bulk.tag")}</label>
          <CrmInput id="crm-bulk-tag" list="crm-bulk-tag-suggestions" value={tag} onChange={(e) => setTag(e.target.value)} autoComplete="off"
            data-testid={CRM.clientsBulkTagInput} />
          <datalist id="crm-bulk-tag-suggestions">{tags.map((x) => <option key={x} value={x} />)}</datalist>
        </>
      )}
      {action === "set_stage" && (
        <>
          <label htmlFor="crm-bulk-stage" className={LABEL}>{t("add.stage")}</label>
          <CrmSelect id="crm-bulk-stage" value={stage} onChange={(e) => setStage(e.target.value)}
            options={STAGES.map((s) => ({ value: s, label: t(STAGE_KEYS[s]) }))} />
          <p className="mt-2 text-xs text-eh-muted">{t("bulk.stageManualHint")}</p>
        </>
      )}
      {action === "add_to_sequence" && (
        sequences && sequences.length === 0
          ? <p className="text-sm text-eh-muted">{t("bulk.noSequences")}</p>
          : (
            <>
              <label htmlFor="crm-bulk-seq" className={LABEL}>{t("bulk.sequence")}</label>
              <CrmSelect id="crm-bulk-seq" value={sequenceId} onChange={(e) => setSequenceId(e.target.value)} placeholder={t("bulk.chooseSequence")}
                options={(sequences || []).map((s) => ({ value: s.id, label: s.name }))} />
              <p className="mt-2 text-xs text-eh-muted">{t("bulk.consentedOnly")}</p>
            </>
          )
      )}
    </CrmDialog>
  );
}

/**
 * "Delete selected" (owner only, owner decision 2026-10-21): the single-client delete,
 * applied to the selection. Confirmed by typing the number of clients selected, in plain
 * digits (a number reads the same in fr/en/es, and a grouped figure such as "1 234" would
 * carry a separator nobody can type). The server re-resolves the selection and refuses
 * with DELETE_COUNT_MISMATCH when its count differs; that refusal, like any other, is
 * shown inside the dialog.
 *
 * `count`: the number selected. `onConfirm()`: performs the delete and resolves on
 * success (the caller closes the dialog) or rejects with the API error.
 */
export function BulkDeleteDialog({ count, countLabel, onClose, onConfirm }) {
  const t = useT("crm");
  const tc = useT("common");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const expected = String(count);
  const matches = count > 0 && typed.trim() === expected;

  const run = async () => {
    if (!matches || busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm();
    } catch (e) {
      setError(crmErrorMessage(e, t, tc));
      setBusy(false);
    }
  };

  return (
    <CrmAlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={t("clientDelete.bulkTitle")}
      testId={CRM.clientsBulkDeleteDialog}
      confirmLabel={t("clientDelete.bulkConfirm")}
      tone="danger"
      busy={busy}
      confirmDisabled={!matches}
      onConfirm={run}
      confirmTestId={CRM.clientsBulkDeleteConfirm}
    >
      <p className="font-semibold">{t("clientDelete.bulkCount", { n: countLabel })}</p>
      <p className="mt-2">{t("clientDelete.bulkBody")}</p>
      <label htmlFor="crm-bulk-delete-count" className="mt-4 block text-xs text-eh-muted">{t("clientDelete.typeCount", { n: expected })}</label>
      <CrmInput id="crm-bulk-delete-count" className="mt-1 w-auto" inputMode="numeric" value={typed} autoComplete="off" invalid={!!error}
        onChange={(e) => { setTyped(e.target.value); setError(""); }} data-testid={CRM.clientsBulkDeleteInput} />
      {error && <p role="alert" className="mt-1 text-xs text-red-700" data-testid={CRM.clientsBulkDeleteError}>{error}</p>}
    </CrmAlertDialog>
  );
}
